import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Pencil, Plus, Upload, Camera, MapPin, Phone, Mail,
  Briefcase, DollarSign, Clock, CheckCircle2, Eye, X, Trash2, Loader2,
} from 'lucide-react';
import TopMenu from '../TopMenu';
import * as api from '../../services/profileApi';
import './Profile.css';

const BACKEND = process.env.REACT_APP_API_BASE_URL
const getPhotoSrc = (url) => (url ? `${BACKEND}${url}` : null);

const EMPTY = {
  firstName: '', surname: '',
  email: '', phone: '', city: '', country: '',
  currentLocation: '', hometown: '',
  experience: '', currentSalary: '', department: '', role: '',
  noticePeriod: '',
  summary: '',
  headline: '',
  photoUrl: null,
  skills: [],
  itSkills: [],
  employments: [],
  educations: [],
  projects: [],
  certifications: [],
  onlineProfiles: [],
  workSamples: [],
  publications: [],
  presentations: [],
  patents: [],
  languages: [],
  dateOfBirth: '', gender: '', maritalStatus: '',
  category: '', differentlyAbled: '',
  careerBreak: '', address: '',
  workPermit: '', industry: '', desiredJobType: '',
  desiredEmploymentType: '', preferredShift: '', preferredLocations: '', expectedSalary: '',
  completionPercent: 0,
};

const QUICK = [
  'Resume', 'Resume headline', 'Key skills', 'Employment', 'Education',
  'IT skills', 'Projects', 'Profile summary', 'Accomplishments',
  'Career profile', 'Personal details',
];

/* ── Field specs per editable section ── */
const FIELD_SPECS = {
  headline:       [{ key: 'headline', label: 'Resume headline', type: 'textarea', rows: 3, placeholder: 'e.g. Frontend Engineer with 3 years of experience in React' }],
  summary:        [{ key: 'summary',  label: 'Profile summary', type: 'textarea', rows: 6, placeholder: 'Describe your professional highlights, interests and goals.' }],
  employments: [
    { key: 'role',        label: 'Designation', placeholder: 'e.g. Software Engineer' },
    { key: 'company',     label: 'Company',     placeholder: 'e.g. Google' },
    { key: 'duration',    label: 'Duration',    placeholder: 'Jan 2022 – Present' },
    { key: 'description', label: 'Description', type: 'textarea', rows: 4 },
  ],
  educations: [
    { key: 'degree',      label: 'Degree / Course', placeholder: 'e.g. B.Tech in CSE' },
    { key: 'institution', label: 'Institute',       placeholder: 'e.g. IIT Delhi' },
    { key: 'year',        label: 'Year',            placeholder: '2022' },
  ],
  itSkills: [
    { key: 'name',       label: 'Skill',      placeholder: 'e.g. React' },
    { key: 'version',    label: 'Version',    placeholder: 'e.g. 18.2' },
    { key: 'lastUsed',   label: 'Last used',  placeholder: 'e.g. 2026' },
    { key: 'experience', label: 'Experience', placeholder: 'e.g. 3 years' },
  ],
  projects: [
    { key: 'title',       label: 'Project title' },
    { key: 'client',      label: 'Client' },
    { key: 'duration',    label: 'Duration' },
    { key: 'description', label: 'Description', type: 'textarea', rows: 4 },
  ],
  onlineProfiles: [{ key: 'name', label: 'Platform', placeholder: 'e.g. LinkedIn' }, { key: 'url',  label: 'URL', placeholder: 'https://...' }],
  workSamples:    [{ key: 'title', label: 'Title' }, { key: 'url',  label: 'URL' }],
  publications:   [{ key: 'title', label: 'Title' }, { key: 'url',  label: 'URL (optional)' }],
  presentations:  [{ key: 'title', label: 'Title' }, { key: 'url',  label: 'URL (optional)' }],
  patents:        [{ key: 'title', label: 'Title' }, { key: 'number', label: 'Patent number' }],
  certifications: [{ key: 'name',  label: 'Certification' }, { key: 'authority', label: 'Issuing authority' }, { key: 'year', label: 'Year' }],
  languages: [
    { key: 'name',        label: 'Language' },
    { key: 'proficiency', label: 'Proficiency', placeholder: 'e.g. Fluent' },
    { key: 'read',        label: 'Read',  type: 'checkbox' },
    { key: 'write',       label: 'Write', type: 'checkbox' },
    { key: 'speak',       label: 'Speak', type: 'checkbox' },
  ],
  career: [
    { key: 'industry',              label: 'Current industry' },
    { key: 'department',            label: 'Department' },
    { key: 'role',                  label: 'Role' },
    { key: 'desiredJobType',        label: 'Desired job type' },
    { key: 'desiredEmploymentType', label: 'Desired employment type' },
    { key: 'preferredShift',        label: 'Preferred shift' },
    { key: 'preferredLocations',    label: 'Preferred work location' },
    { key: 'expectedSalary',        label: 'Expected salary' },
  ],
  personal: [
    { key: 'gender',           label: 'Gender' },
    { key: 'maritalStatus',    label: 'Marital status' },
    { key: 'dateOfBirth',      label: 'Date of birth', type: 'date' },
    { key: 'category',         label: 'Category' },
    { key: 'differentlyAbled', label: 'Differently abled' },
    { key: 'careerBreak',      label: 'Career break' },
    { key: 'workPermit',       label: 'Work permit' },
    { key: 'address',          label: 'Address', type: 'textarea', rows: 2 },
    { key: 'hometown',         label: 'Hometown' },
  ],
  basic: [
    { key: 'firstName',       label: 'First name' },
    { key: 'surname',         label: 'Last name' },
    { key: 'phone',           label: 'Phone' },
    { key: 'currentLocation', label: 'Current location' },
    { key: 'experience',      label: 'Experience' },
    { key: 'currentSalary',   label: 'Current salary' },
    { key: 'noticePeriod',    label: 'Notice period' },
  ],
};

const Row = ({ label, value, empty = '—' }) => (
  <div className="np-row">
    <div className="np-row-label">{label}</div>
    <div className={`np-row-value ${value ? '' : 'np-row-value-empty'}`}>
      {value || <span className="np-empty">{empty}</span>}
    </div>
  </div>
);

const Section = ({ title, onAdd, onEdit, subtitle, children, id }) => (
  <section className="np-section" id={id}>
    <div className="np-section-head">
      <div>
        <h2>{title}</h2>
        {subtitle && <p className="np-section-sub">{subtitle}</p>}
      </div>
      <div className="np-section-actions">
        {onAdd && <button className="np-btn-link" onClick={onAdd}><Plus size={14} /> Add</button>}
        {onEdit && <button className="np-btn-icon" onClick={onEdit} title="Edit"><Pencil size={14} /></button>}
      </div>
    </div>
    <div className="np-section-body">{children}</div>
  </section>
);

/* ── Modal primitives ── */
const Modal = ({ title, onClose, children, footer, size = 'md' }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="np-modal-backdrop" onClick={onClose}>
      <div className={`np-modal np-modal-${size}`} onClick={(e) => e.stopPropagation()}>
        <div className="np-modal-head">
          <h3>{title}</h3>
          <button className="np-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="np-modal-body">{children}</div>
        {footer && <div className="np-modal-foot">{footer}</div>}
      </div>
    </div>
  );
};

const FormField = ({ spec, value, onChange }) => {
  const { key, label, type = 'text', placeholder, rows } = spec;
  if (type === 'textarea') {
    return (
      <div className="np-f">
        <label>{label}</label>
        <textarea rows={rows || 3} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(key, e.target.value)} />
      </div>
    );
  }
  if (type === 'checkbox') {
    return (
      <label className="np-f-check">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(key, e.target.checked)} />
        <span>{label}</span>
      </label>
    );
  }
  return (
    <div className="np-f">
      <label>{label}</label>
      <input type={type} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(key, e.target.value)} />
    </div>
  );
};

/* ── Photo upload modal ── */
const PhotoModal = ({ photo, busy, onClose, onReplace, onDelete }) => {
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Photo must be under 2 MB');
      return;
    }
    onReplace(file);
  };
  return (
    <Modal title="Profile photo upload" onClose={onClose} size="sm">
      <div className="np-photo-modal">
        <div className="np-photo-preview">
          {photo
            ? <img src={photo} alt="Profile" />
            : <div className="np-photo-placeholder"><Camera size={32} /></div>}
        </div>
        <label className={`np-btn-primary np-photo-upload ${busy ? 'is-busy' : ''}`}>
          {busy ? <><Loader2 size={14} className="np-spin" /> Uploading…</> : 'Replace photo'}
          <input type="file" accept="image/png,image/jpeg,image/jpg,image/gif" onChange={handleFile} hidden disabled={busy} />
        </label>
        <p className="np-photo-or">
          or <button className="np-link" onClick={onDelete} disabled={!photo || busy}>Delete</button>
        </p>
        <p className="np-photo-hint">Supported file format: png, jpg, jpeg, gif — upto 2MB</p>
        <button type="button" className="np-link np-photo-tos">Terms of services</button>
      </div>
    </Modal>
  );
};

/* ── Edit modal (add / edit multi-field) ── */
const EditModal = ({ title, fields, values, busy, onSave, onClose }) => {
  const [draft, setDraft] = useState(() => {
    const init = {};
    for (const f of fields) init[f.key] = values?.[f.key] ?? (f.type === 'checkbox' ? false : '');
    return init;
  });
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="np-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="np-btn-primary" onClick={() => onSave(draft)} disabled={busy}>
            {busy ? <><Loader2 size={14} className="np-spin" /> Saving…</> : 'Save'}
          </button>
        </>
      }
    >
      <div className="np-form">
        {fields.map((f) => (
          <FormField key={f.key} spec={f} value={draft[f.key]} onChange={set} />
        ))}
      </div>
    </Modal>
  );
};

/* ── Skills (chip-list) modal ── */
const ListEditModal = ({ title, items = [], busy, onSave, onClose, placeholder }) => {
  const [list, setList] = useState(items);
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (!v) return;
    setList([...list, v]);
    setInput('');
  };
  const remove = (i) => setList(list.filter((_, idx) => idx !== i));
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="np-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="np-btn-primary" onClick={() => onSave(list)} disabled={busy}>
            {busy ? <><Loader2 size={14} className="np-spin" /> Saving…</> : 'Save'}
          </button>
        </>
      }
    >
      <div className="np-form">
        <div className="np-f">
          <label>Add</label>
          <div className="np-chip-input">
            <input
              type="text"
              value={input}
              placeholder={placeholder || 'Type and press Enter'}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            />
            <button className="np-btn-primary np-btn-sm" onClick={add}>Add</button>
          </div>
        </div>
        <div className="np-chips">
          {list.map((s, i) => (
            <span key={i} className="np-chip">
              {s}
              <button className="np-chip-x" onClick={() => remove(i)} aria-label="Remove"><X size={12} /></button>
            </span>
          ))}
          {list.length === 0 && <span className="np-empty">No items yet.</span>}
        </div>
      </div>
    </Modal>
  );
};

const Profile = ({ theme = 'light', toggleTheme }) => {
  const [data, setData] = useState(EMPTY);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [publicModal, setPublicModal] = useState(null);
  const [publicUrl, setPublicUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [resumeUploading, setResumeUploading] = useState(false);
  const navigate = useNavigate();
  const dark = theme === 'dark';

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    api.getProfile()
      .then(data => {
        setData({ ...EMPTY, ...data });
      })
      .catch(() => showToast('Failed to load profile', 'error'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const completion = data.completionPercent ?? 0;
  const fullName = [data.firstName, data.surname].filter(Boolean).join(' ') || 'Your Name';
  const photoDisplay = getPhotoSrc(data.photoUrl);

  /* ── Modal openers ── */
  const openFieldsModal = (title, fields, section) =>
    setModal({ kind: 'edit', title, fields, section, values: data });
  const openAddListModal = (title, listKey, fields) =>
    setModal({ kind: 'add-list', title, listKey, fields });
  const openEditListItemModal = (title, listKey, index, fields) =>
    setModal({ kind: 'edit-list', title, listKey, index, fields, values: data[listKey][index] });
  const openSkillsModal = () =>
    setModal({ kind: 'skills', items: (data.skills || []).map(s => (typeof s === 'string' ? s : s.name)) });
  const openPhoto = () => setModal({ kind: 'photo' });
  const closeModal = () => { if (!saving) setModal(null); };

  /* ── Save handlers ── */
  const saveBasic = async (draft) => {
    setSaving(true);
    try {
      const res = await api.updateBasic(draft);
      setData({ ...EMPTY, ...res });
      showToast('Basic details updated');
    } catch { showToast('Failed to save', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const saveHeadline = async (draft) => {
    setSaving(true);
    try {
      const res = await api.updateHeadline({ headline: draft.headline });
      setData({ ...EMPTY, ...res });
      showToast('Headline updated');
    } catch { showToast('Failed to save', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const saveSummary = async (draft) => {
    setSaving(true);
    try {
      const res = await api.updateSummary({ summary: draft.summary });
      setData({ ...EMPTY, ...res });
      showToast('Summary updated');
    } catch { showToast('Failed to save', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const saveCareer = async (draft) => {
    setSaving(true);
    try {
      const res = await api.updateCareer(draft);
      setData({ ...EMPTY, ...res });
      showToast('Career profile updated');
    } catch { showToast('Failed to save', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const savePersonal = async (draft) => {
    setSaving(true);
    try {
      const res = await api.updatePersonal(draft);
      setData({ ...EMPTY, ...res });
      showToast('Personal details updated');
    } catch { showToast('Failed to save', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const saveSkillsList = async (list) => {
    setSaving(true);
    try {
      const res = await api.saveSkills(list);
      setData({ ...EMPTY, ...res });
      showToast('Skills updated');
    } catch { showToast('Failed to save', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const savePhoto = async (file) => {
    setSaving(true);
    try {
      const res = await api.updatePhoto(file);
      setData(prev => ({ ...prev, photoUrl: res.photoUrl }));
      showToast('Photo updated');
    } catch { showToast('Failed to upload photo', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const handleDeletePhoto = async () => {
    setSaving(true);
    try {
      await api.deletePhoto();
      setData(prev => ({ ...prev, photoUrl: null }));
      showToast('Photo removed');
    } catch { showToast('Failed to delete photo', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const saveAddList = async (listKey, draft) => {
    setSaving(true);
    try {
      let item;
      if (listKey === 'employments') item = await api.addEmployment(draft);
      else if (listKey === 'educations') item = await api.addEducation(draft);
      else if (listKey === 'itSkills') item = await api.addItSkill(draft);
      else if (listKey === 'projects') item = await api.addProject(draft);
      else if (listKey === 'certifications') item = await api.addCertification(draft);
      else if (listKey === 'onlineProfiles') item = await api.addOnlineProfile(draft);
      else if (listKey === 'workSamples') item = await api.addWorkSample(draft);
      else if (listKey === 'publications') item = await api.addPublication(draft);
      else if (listKey === 'presentations') item = await api.addPresentation(draft);
      else if (listKey === 'patents') item = await api.addPatent(draft);
      else if (listKey === 'languages') item = await api.addLanguage(draft);
      setData(prev => ({ ...prev, [listKey]: [...(prev[listKey] || []), item] }));
      showToast('Added successfully');
    } catch { showToast('Failed to add', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const saveEditList = async (listKey, index, draft) => {
    const id = data[listKey][index].id;
    setSaving(true);
    try {
      let item;
      if (listKey === 'employments') item = await api.updateEmployment(id, draft);
      else if (listKey === 'educations') item = await api.updateEducation(id, draft);
      else if (listKey === 'itSkills') item = await api.updateItSkill(id, draft);
      else if (listKey === 'projects') item = await api.updateProject(id, draft);
      else if (listKey === 'languages') item = await api.updateLanguage(id, draft);
      const arr = [...data[listKey]];
      arr[index] = item;
      setData(prev => ({ ...prev, [listKey]: arr }));
      showToast('Updated successfully');
    } catch { showToast('Failed to update', 'error'); }
    setSaving(false);
    setModal(null);
  };

  const removeListItem = async (listKey, index) => {
    const id = data[listKey][index].id;
    try {
      if (listKey === 'employments') await api.deleteEmployment(id);
      else if (listKey === 'educations') await api.deleteEducation(id);
      else if (listKey === 'itSkills') await api.deleteItSkill(id);
      else if (listKey === 'projects') await api.deleteProject(id);
      else if (listKey === 'certifications') await api.deleteCertification(id);
      else if (listKey === 'onlineProfiles') await api.deleteOnlineProfile(id);
      else if (listKey === 'workSamples') await api.deleteWorkSample(id);
      else if (listKey === 'publications') await api.deletePublication(id);
      else if (listKey === 'presentations') await api.deletePresentation(id);
      else if (listKey === 'patents') await api.deletePatent(id);
      else if (listKey === 'languages') await api.deleteLanguage(id);
      setData(prev => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== index) }));
      showToast('Deleted');
    } catch { showToast('Failed to delete', 'error'); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div className={`np-page ${dark ? 'np-dark' : 'np-light'}`}>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8, fontWeight: 500, fontSize: 14,
          background: toast.type === 'error' ? '#fee2e2' : '#dcfce7',
          color: toast.type === 'error' ? '#991b1b' : '#166534',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transition: 'all 0.3s ease'
        }}>
          {toast.msg}
        </div>
      )}

      <TopMenu theme={theme} toggleTheme={toggleTheme} />

      <div className="np-wrap">

        {/* ── TOP BANNER ── */}
        <div className="np-top-card">
          <div className="np-top-left">
            <div className="np-avatar-wrap" onClick={openPhoto} role="button" tabIndex={0}>
              <div className="np-avatar">
                {photoDisplay
                  ? <img src={photoDisplay} alt={fullName} />
                  : (data.firstName?.[0] || 'U').toUpperCase()}
              </div>
              <div className="np-avatar-overlay">
                <Camera size={18} />
                <span>Change</span>
              </div>
            </div>
            <div className="np-top-info">
              <div className="np-top-name-row">
                <h1>{fullName}</h1>
                <button className="np-btn-icon" onClick={() => openFieldsModal('Basic details', FIELD_SPECS.basic, 'basic')}>
                  <Pencil size={14} />
                </button>
              </div>
              <p className="np-top-headline">{data.headline || 'Add a resume headline'}</p>
              <div className="np-top-meta">
                <span><Briefcase size={13} /> {data.experience || 'Fresher'}</span>
                <span><DollarSign size={13} /> {data.currentSalary || 'Not disclosed'}</span>
                <span><MapPin size={13} /> {data.currentLocation || data.city || 'Add location'}</span>
              </div>
              <div className="np-top-meta">
                <span><Mail size={13} /> {data.email || '—'} {data.email && <CheckCircle2 size={12} className="np-verified" />}</span>
                <span><Phone size={13} /> {data.phone || 'Add phone'}</span>
                <span><Clock size={13} /> {data.noticePeriod || '15 Days or less'}</span>
              </div>
            </div>
          </div>
          <div className="np-top-right">
            <div className="np-profile-meter">
              <svg viewBox="0 0 120 120" className="np-meter-svg">
                <circle cx="60" cy="60" r="52" className="np-meter-bg" />
                <circle
                  cx="60" cy="60" r="52"
                  className="np-meter-fg"
                  strokeDasharray={`${(completion / 100) * 326.7} 326.7`}
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="np-meter-text">
                <strong>{completion}%</strong>
                <span>Profile</span>
              </div>
            </div>
            <button className="np-btn-outline" onClick={() => setPublicModal('confirm')}>
              <Eye size={13} /> View public profile
            </button>
          </div>
        </div>

        {/* ── MAIN GRID ── */}
        <div className="np-grid">
          <aside className="np-side">
            <h3>Quick links</h3>
            <ul className="np-quick">
              {QUICK.map((q) => {
                const id = q.toLowerCase().replace(/\s+/g, '-');
                return <li key={q}><a href={`#${id}`}>{q}</a></li>;
              })}
            </ul>
          </aside>

          <main className="np-main">

            <Section title="Resume" id="resume" subtitle="Resume is the most important document recruiters look for. Upload your resume to let recruiters find you.">
              <div className="np-resume-box">
                <div className="np-resume-icon"><Upload size={22} /></div>
                <div className="np-resume-copy">
                  <p className="np-resume-title">Upload resume</p>
                  <p className="np-resume-hint">Supported formats: DOC, DOCX, PDF — up to 5 MB</p>
                </div>
                <label className={`np-btn-primary ${resumeUploading ? 'np-btn-loading' : ''}`} style={{ cursor: resumeUploading ? 'wait' : 'pointer' }}>
                  {resumeUploading ? 'Parsing...' : 'Upload & Prefill'}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    hidden
                    disabled={resumeUploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setResumeUploading(true);
                      try {
                        const res = await api.uploadAndPrefill(file);
                        setData({ ...EMPTY, ...res });
                        showToast('Profile prefilled from resume!');
                      } catch {
                        showToast('Failed to parse resume. Try again.', 'error');
                      } finally {
                        setResumeUploading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
              </div>
            </Section>

            <Section
              title="Resume headline"
              id="resume-headline"
              onEdit={() => openFieldsModal('Resume headline', FIELD_SPECS.headline, 'headline')}
            >
              <p className="np-body-text">{data.headline || 'Add a short summary that describes your profile and career goals.'}</p>
            </Section>

            <Section
              title="Key skills"
              id="key-skills"
              subtitle="Help employers search your profile and match you with the right jobs."
              onAdd={openSkillsModal}
              onEdit={(data.skills || []).length ? openSkillsModal : undefined}
            >
              {(data.skills || []).length ? (
                <div className="np-chips">
                  {data.skills.map((s, i) => (
                    <span key={s.id ?? i} className="np-chip np-chip-static">
                      {typeof s === 'string' ? s : s.name}
                    </span>
                  ))}
                </div>
              ) : <p className="np-empty">Add your key skills to get noticed by recruiters.</p>}
            </Section>

            <Section
              title="Employment"
              id="employment"
              onAdd={() => openAddListModal('Add employment', 'employments', FIELD_SPECS.employments)}
            >
              {data.employments.length ? data.employments.map((e, i) => (
                <div key={e.id ?? i} className="np-item">
                  <div className="np-item-head">
                    <h4>{e.role} <span className="np-sep">at</span> {e.company}</h4>
                    <div className="np-item-actions">
                      <button className="np-btn-icon" onClick={() => openEditListItemModal('Edit employment', 'employments', i, FIELD_SPECS.employments)}><Pencil size={13} /></button>
                      <button className="np-btn-icon np-btn-danger" onClick={() => removeListItem('employments', i)}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <p className="np-muted">{e.duration}</p>
                  <p className="np-body-text">{e.description}</p>
                </div>
              )) : <p className="np-empty">Add your work experience so recruiters can evaluate your career.</p>}
            </Section>

            <Section
              title="Education"
              id="education"
              onAdd={() => openAddListModal('Add education', 'educations', FIELD_SPECS.educations)}
            >
              {data.educations.length ? data.educations.map((e, i) => (
                <div key={e.id ?? i} className="np-item">
                  <div className="np-item-head">
                    <h4>{e.degree}</h4>
                    <div className="np-item-actions">
                      <button className="np-btn-icon" onClick={() => openEditListItemModal('Edit education', 'educations', i, FIELD_SPECS.educations)}><Pencil size={13} /></button>
                      <button className="np-btn-icon np-btn-danger" onClick={() => removeListItem('educations', i)}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <p className="np-muted">{e.institution} · {e.year}</p>
                </div>
              )) : <p className="np-empty">Add your education details.</p>}
            </Section>

            <Section
              title="IT skills"
              id="it-skills"
              onAdd={() => openAddListModal('Add IT skill', 'itSkills', FIELD_SPECS.itSkills)}
            >
              {data.itSkills.length ? (
                <table className="np-table">
                  <thead>
                    <tr><th>Skill</th><th>Version</th><th>Last Used</th><th>Experience</th><th></th></tr>
                  </thead>
                  <tbody>
                    {data.itSkills.map((s, i) => (
                      <tr key={s.id ?? i}>
                        <td>{s.name}</td><td>{s.version}</td><td>{s.lastUsed}</td><td>{s.experience}</td>
                        <td className="np-td-actions">
                          <button className="np-btn-icon" onClick={() => openEditListItemModal('Edit IT skill', 'itSkills', i, FIELD_SPECS.itSkills)}><Pencil size={12} /></button>
                          <button className="np-btn-icon np-btn-danger" onClick={() => removeListItem('itSkills', i)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="np-empty">Add software, languages and frameworks you've worked with.</p>}
            </Section>

            <Section
              title="Projects"
              id="projects"
              onAdd={() => openAddListModal('Add project', 'projects', FIELD_SPECS.projects)}
            >
              {data.projects.length ? data.projects.map((p, i) => (
                <div key={p.id ?? i} className="np-item">
                  <div className="np-item-head">
                    <h4>{p.title}</h4>
                    <div className="np-item-actions">
                      <button className="np-btn-icon" onClick={() => openEditListItemModal('Edit project', 'projects', i, FIELD_SPECS.projects)}><Pencil size={13} /></button>
                      <button className="np-btn-icon np-btn-danger" onClick={() => removeListItem('projects', i)}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <p className="np-muted">{p.client} · {p.duration}</p>
                  <p className="np-body-text">{p.description}</p>
                </div>
              )) : <p className="np-empty">Add projects you've worked on to strengthen your profile.</p>}
            </Section>

            <Section
              title="Profile summary"
              id="profile-summary"
              onEdit={() => openFieldsModal('Profile summary', FIELD_SPECS.summary, 'summary')}
              subtitle="Your profile summary should mention the highlights of your career and education, what your professional interests are, and what kind of a career you are looking for."
            >
              <p className="np-body-text">{data.summary || 'Add a compelling profile summary.'}</p>
            </Section>

            <Section title="Accomplishments" id="accomplishments" subtitle="Showcase your credentials by adding relevant certifications, work samples, online profiles, etc.">
              <div className="np-acc-list">
                <AccItem title="Online profile" hint="Add link to online profiles (e.g. LinkedIn, Facebook etc.)" onAdd={() => openAddListModal('Add online profile', 'onlineProfiles', FIELD_SPECS.onlineProfiles)} items={data.onlineProfiles} render={p => `${p.name} — ${p.url}`} onRemove={(i) => removeListItem('onlineProfiles', i)} />
                <AccItem title="Work sample" hint="Link to your projects (e.g. blog, portfolio, GitHub)." onAdd={() => openAddListModal('Add work sample', 'workSamples', FIELD_SPECS.workSamples)} items={data.workSamples} render={w => `${w.title} — ${w.url}`} onRemove={(i) => removeListItem('workSamples', i)} />
                <AccItem title="White paper / Research publication / Journal entry" hint="Highlight publications in journals, books and magazines." onAdd={() => openAddListModal('Add publication', 'publications', FIELD_SPECS.publications)} items={data.publications} render={p => p.title} onRemove={(i) => removeListItem('publications', i)} />
                <AccItem title="Presentation" hint="Add link to a presentation you've created or worked on." onAdd={() => openAddListModal('Add presentation', 'presentations', FIELD_SPECS.presentations)} items={data.presentations} render={p => p.title} onRemove={(i) => removeListItem('presentations', i)} />
                <AccItem title="Patent" hint="Showcase your unique inventions that are officially patented." onAdd={() => openAddListModal('Add patent', 'patents', FIELD_SPECS.patents)} items={data.patents} render={p => p.title} onRemove={(i) => removeListItem('patents', i)} />
                <AccItem title="Certification" hint="Add details of relevant certifications that showcase your skills." onAdd={() => openAddListModal('Add certification', 'certifications', FIELD_SPECS.certifications)} items={data.certifications} render={c => `${c.name} — ${c.authority}`} onRemove={(i) => removeListItem('certifications', i)} />
              </div>
            </Section>

            <Section
              title="Career profile"
              id="career-profile"
              onEdit={() => openFieldsModal('Career profile', FIELD_SPECS.career, 'career')}
            >
              <div className="np-detail-grid">
                <Row label="Current industry" value={data.industry} />
                <Row label="Department" value={data.department} />
                <Row label="Role" value={data.role} />
                <Row label="Desired job type" value={data.desiredJobType} />
                <Row label="Desired employment type" value={data.desiredEmploymentType} />
                <Row label="Preferred shift" value={data.preferredShift} />
                <Row label="Preferred work location" value={data.preferredLocations} />
                <Row label="Expected salary" value={data.expectedSalary} />
              </div>
            </Section>

            <Section
              title="Personal details"
              id="personal-details"
              onEdit={() => openFieldsModal('Personal details', FIELD_SPECS.personal, 'personal')}
            >
              <div className="np-detail-grid">
                <Row label="Gender" value={data.gender} />
                <Row label="Marital status" value={data.maritalStatus} />
                <Row label="Date of birth" value={data.dateOfBirth ? (data.dateOfBirth || '').slice(0, 10) : ''} />
                <Row label="Category" value={data.category} />
                <Row label="Differently abled" value={data.differentlyAbled} />
                <Row label="Career break" value={data.careerBreak} />
                <Row label="Work permit" value={data.workPermit} />
                <Row label="Address" value={data.address} />
                <Row label="Hometown" value={data.hometown} />
              </div>
            </Section>

            <Section
              title="Language"
              id="language"
              onAdd={() => openAddListModal('Add language', 'languages', FIELD_SPECS.languages)}
            >
              {data.languages.length ? (
                <table className="np-table">
                  <thead>
                    <tr><th>Language</th><th>Proficiency</th><th>Read</th><th>Write</th><th>Speak</th><th></th></tr>
                  </thead>
                  <tbody>
                    {data.languages.map((l, i) => (
                      <tr key={l.id ?? i}>
                        <td>{l.name}</td><td>{l.proficiency}</td>
                        <td>{l.read ? '✓' : '—'}</td><td>{l.write ? '✓' : '—'}</td><td>{l.speak ? '✓' : '—'}</td>
                        <td className="np-td-actions">
                          <button className="np-btn-icon" onClick={() => openEditListItemModal('Edit language', 'languages', i, FIELD_SPECS.languages)}><Pencil size={12} /></button>
                          <button className="np-btn-icon np-btn-danger" onClick={() => removeListItem('languages', i)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="np-empty">Add languages you know.</p>}
            </Section>

          </main>
        </div>
      </div>

      {/* ── Modals ── */}
      {modal?.kind === 'photo' && (
        <PhotoModal
          photo={photoDisplay}
          busy={saving}
          onClose={closeModal}
          onReplace={savePhoto}
          onDelete={handleDeletePhoto}
        />
      )}
      {modal?.kind === 'edit' && (
        <EditModal
          title={modal.title}
          fields={modal.fields}
          values={modal.values}
          busy={saving}
          onSave={(draft) => {
            if (modal.section === 'basic') saveBasic(draft);
            else if (modal.section === 'headline') saveHeadline(draft);
            else if (modal.section === 'summary') saveSummary(draft);
            else if (modal.section === 'career') saveCareer(draft);
            else if (modal.section === 'personal') savePersonal(draft);
          }}
          onClose={closeModal}
        />
      )}
      {modal?.kind === 'add-list' && (
        <EditModal
          title={modal.title}
          fields={modal.fields}
          values={{}}
          busy={saving}
          onSave={(draft) => saveAddList(modal.listKey, draft)}
          onClose={closeModal}
        />
      )}
      {modal?.kind === 'edit-list' && (
        <EditModal
          title={modal.title}
          fields={modal.fields}
          values={modal.values}
          busy={saving}
          onSave={(draft) => saveEditList(modal.listKey, modal.index, draft)}
          onClose={closeModal}
        />
      )}
      {modal?.kind === 'skills' && (
        <ListEditModal
          title="Key skills"
          items={modal.items}
          busy={saving}
          placeholder="e.g. React, Python, Figma"
          onSave={saveSkillsList}
          onClose={closeModal}
        />
      )}

      {/* Confirm generate link */}
      {publicModal === 'confirm' && (
        <Modal title="Share Your Profile" onClose={() => setPublicModal(null)} size="sm"
          footer={
            <>
              <button className="np-btn-ghost" onClick={() => setPublicModal(null)}>Cancel</button>
              <button className="np-btn-primary" onClick={async () => {
                try {
                  const res = await api.generatePublicLink();
                  const frontendBase = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
                  const frontendUrl = `${frontendBase}/public/profile/${res.token}`;
                  setPublicUrl(frontendUrl);
                  setPublicModal('link');
                  setCopied(false);
                } catch { showToast('Failed to generate link', 'error'); setPublicModal(null); }
              }}>
                Generate Link
              </button>
            </>
          }
        >
          <p style={{ marginBottom: 8 }}>Generate a public link to your profile?</p>
          <p style={{ color: '#6b7280', fontSize: 13 }}>
            Anyone with this link can view your profile without logging in.
          </p>
        </Modal>
      )}

      {/* Show link */}
      {publicModal === 'link' && (
        <Modal title="Your Public Profile Link" onClose={() => setPublicModal(null)} size="sm"
          footer={<button className="np-btn-ghost" onClick={() => setPublicModal(null)}>Done</button>}
        >
          <p style={{ marginBottom: 12, color: '#6b7280', fontSize: 13 }}>
            Anyone with this link can view your profile.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              readOnly
              value={publicUrl}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
            />
            <button className="np-btn-primary" onClick={() => {
              navigator.clipboard.writeText(publicUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const AccItem = ({ title, hint, items = [], render, onAdd, onRemove }) => (
  <div className="np-acc-item">
    <div className="np-acc-head">
      <div>
        <p className="np-acc-title">{title}</p>
        <p className="np-acc-hint">{hint}</p>
      </div>
      <button className="np-btn-link" onClick={onAdd}><Plus size={12} /> Add</button>
    </div>
    {items.length > 0 && (
      <ul className="np-acc-values">
        {items.map((it, i) => (
          <li key={it.id ?? i}>
            <span>{render(it)}</span>
            <button className="np-btn-icon np-btn-danger" onClick={() => onRemove(i)}><Trash2 size={12} /></button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default Profile;
